import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getRealConsumptionByPeriod } from "@/services/apiCallsService";
import { isAuthenticated } from "@/services/tokenService";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";

interface TokenUsageChartProps {
  clientId?: string;
  workspaceId?: string;
}

type ChartRow = { name: string; input: number; output: number; cost: number };

export const TokenUsageChart = ({
  clientId,
}: TokenUsageChartProps = {}) => {
  const [timeFilter, setTimeFilter] = useState<"1h" | "24h" | "7d" | "30d">("24h");
  const [chartData, setChartData] = useState<ChartRow[]>([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [providerCost, setProviderCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isAdminOrSupport } = usePermissions();

  useEffect(() => {
    if (isAuthenticated()) {
      loadChartData();
    } else {
      setLoading(false);
      setChartData([]);
      setTotalTokens(0);
      setProviderCost(0);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, clientId]);

  const resolveClientIdParam = (): string | undefined => {
    if (clientId === "null") return "null";
    if (clientId && clientId !== "all") return clientId;
    return undefined;
  };

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getRealConsumptionByPeriod({
        period: timeFilter,
        client_id: resolveClientIdParam(),
      });

      if (!response.success || !response.data) {
        const message =
          response.error?.message || "Erro ao carregar dados do gráfico";
        setError(message);
        setChartData([]);
        setTotalTokens(0);
        setProviderCost(0);
        toast({
          title: "Erro",
          description: message,
          variant: "destructive",
        });
        return;
      }

      const { by_model, total_tokens, cost } = response.data;
      const rows: ChartRow[] = (by_model || [])
        .filter((row) => row.input_tokens > 0 || row.output_tokens > 0)
        .map((row) => ({
          name: row.model_name,
          input: row.input_tokens,
          output: row.output_tokens,
          cost: row.cost,
        }));

      setChartData(rows);
      setTotalTokens(total_tokens || 0);
      setProviderCost(cost || 0);
    } catch {
      const message = "Erro ao carregar dados do gráfico";
      setError(message);
      setChartData([]);
      setTotalTokens(0);
      setProviderCost(0);
      toast({
        title: "Erro",
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const getDataAndTitle = () => {
    switch (timeFilter) {
      case "1h":
        return {
          title: "Uso de Tokens por Modelo (1h)",
          description:
            "Tokens reais de entrada vs saída por modelo na última 1 hora",
        };
      case "24h":
        return {
          title: "Uso de Tokens por Modelo (24h)",
          description:
            "Tokens reais de entrada vs saída por modelo nas últimas 24 horas",
        };
      case "7d":
        return {
          title: "Uso de Tokens por Modelo (7 dias)",
          description:
            "Tokens reais de entrada vs saída por modelo nos últimos 7 dias",
        };
      case "30d":
        return {
          title: "Uso de Tokens por Modelo (30 dias)",
          description:
            "Tokens reais de entrada vs saída por modelo nos últimos 30 dias",
        };
      default:
        return {
          title: "Uso de Tokens por Modelo (24h)",
          description:
            "Tokens reais de entrada vs saída por modelo nas últimas 24 horas",
        };
    }
  };

  const { title, description } = getDataAndTitle();

  return (
    <Card className="col-span-1">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
            {!loading && !error && totalTokens > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Total: {totalTokens.toLocaleString()} tokens
                {isAdminOrSupport && (
                  <> · Custo provedor: ${providerCost.toFixed(6)}</>
                )}
              </p>
            )}
          </div>
          <Select
            value={timeFilter}
            onValueChange={(value: string) =>
              setTimeFilter(value as "1h" | "24h" | "7d" | "30d")
            }
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">1 hora</SelectItem>
              <SelectItem value="24h">24 horas</SelectItem>
              <SelectItem value="7d">7 dias</SelectItem>
              <SelectItem value="30d">30 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-sm text-muted-foreground">Carregando dados...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-sm text-destructive text-center px-4">
              Não foi possível carregar o consumo real.
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-sm text-muted-foreground text-center px-4">
              Nenhuma chamada no período selecionado.
              <br />
              Tokens e custo: 0
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value, name) => {
                  if (name === "cost") {
                    return [
                      `$${Number(value || 0).toFixed(6)}`,
                      "Custo provedor",
                    ];
                  }
                  return [
                    Number(value || 0).toLocaleString(),
                    name === "input" ? "Token de Entrada" : "Token de Saída",
                  ];
                }}
              />
              <Legend
                formatter={(value) =>
                  value === "input"
                    ? "Token de Entrada"
                    : value === "output"
                      ? "Token de Saída"
                      : "Custo"
                }
                iconType="rect"
                wrapperStyle={{
                  paddingTop: "20px",
                }}
              />
              <Bar
                dataKey="input"
                fill="hsl(var(--primary))"
                name="input"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="output"
                fill="hsl(var(--accent))"
                name="output"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
